import { useState, useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import {
  Star,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  Layers,
  Box,
  Palette,
  Home,
  MessageSquare,
  Pencil,
  Hammer,
  Award,
  Users,
  TrendingUp,
  Shield,
  ArrowRight,
  Quote,
  Clock,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLanguage } from '@/i18n/LanguageContext';
import { COMPANY_INFO } from '@/lib/index';
import { services, portfolioProjects, reviews, pricingTiers, faqItems, processSteps, blogPosts } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { ServiceModal, ProjectModal, BlogModal } from '@/components/Modals';
import type { Service, Project, BlogPost } from '@/lib/index';

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Layers, Box, Palette, Home, MessageSquare, Pencil, Hammer, CheckCircle,
  Award, Users, TrendingUp, Shield,
};

/* ─── Animation Variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 280, damping: 28 } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 280, damping: 28 } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 280, damping: 28 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 320, damping: 28 } },
};

/* ─── Animated Counter ─── */
function AnimatedCounter({ value, prefix = '', suffix = '' }: { value: number; prefix?: string; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const motionVal = useMotionValue(0);
  const springVal = useSpring(motionVal, { stiffness: 80, damping: 20 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (inView) motionVal.set(value);
  }, [inView, motionVal, value]);

  useEffect(() => {
    const unsub = springVal.on('change', (v) => setDisplay(Math.round(v)));
    return unsub;
  }, [springVal]);

  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

/* ═══════════════════════════════════════════════ */
/*  HERO                                           */
/* ═══════════════════════════════════════════════ */
export function HeroSection() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-secondary">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.HERO_KITCHEN_3}
          alt="Luxury Kitchen"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/60 via-secondary/40 to-secondary/80" />
        {/* Animated gold shimmer line */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 3 }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          <Badge className="mb-8 bg-primary/20 text-primary border border-primary/40 backdrop-blur-sm px-6 py-2 text-sm font-medium tracking-wider">
            <Sparkles className="w-4 h-4 mr-2 inline" />
            {t('hero.badge')}
          </Badge>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28, delay: 0.1 }}
        >
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-secondary-foreground mb-6 leading-none tracking-tight"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            {t('hero.headline1')}
            <br />
            <span className="text-primary">{t('hero.headline2')}</span>
          </h1>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          className="text-lg md:text-xl text-secondary-foreground/60 max-w-2xl mx-auto mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.6 }}
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28, delay: 0.35 }}
        >
          <Button
            size="lg"
            onClick={() => scrollToSection('contact')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 transition-all duration-300"
          >
            {t('hero.ctaPrimary')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => scrollToSection('portfolio')}
            className="border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 hover:border-primary px-8 py-6 text-base backdrop-blur-sm hover:-translate-y-1 transition-all duration-300"
          >
            {t('hero.ctaSecondary')}
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28, delay: 0.5 }}
        >
          {[
            { val: t('hero.stats.projectsValue'), label: t('hero.stats.projects') },
            { val: t('hero.stats.experienceValue'), label: t('hero.stats.experience') },
            { val: t('hero.stats.priceRangeValue'), label: t('hero.stats.priceRange') },
            { val: t('hero.stats.ratingValue'), label: t('hero.stats.rating') },
          ].map((stat, i) => (
            <div key={i} className="text-center group">
              <div
                className="text-3xl md:text-4xl font-bold text-primary mb-1 group-hover:scale-110 transition-transform duration-300"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {stat.val}
              </div>
              <div className="text-xs uppercase tracking-widest text-secondary-foreground/50 font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        onClick={() => scrollToSection('about')}
      >
        <span className="text-[10px] uppercase tracking-widest text-secondary-foreground/40 font-medium">{t('hero.scrollIndicator')}</span>
        <ChevronDown className="w-5 h-5 text-primary opacity-60" />
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  ABOUT                                          */
/* ═══════════════════════════════════════════════ */
export function AboutSection() {
  const { t } = useLanguage();

  const values = [
    { icon: Award, key: 'quality' },
    { icon: Shield, key: 'transparency' },
    { icon: TrendingUp, key: 'craftsmanship' },
  ] as const;

  return (
    <section className="py-28 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid lg:grid-cols-2 gap-16 items-start"
        >
          {/* Text side */}
          <motion.div variants={fadeLeft}>
            <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-4">{t('about.subtitle')}</p>
            <h2
              className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {t('about.title')}
            </h2>
            <div className="w-16 h-1 bg-primary mb-8 rounded-full" />
            <p className="text-muted-foreground leading-relaxed mb-5 text-lg">
              {t('about.description')}
            </p>
            <p className="text-muted-foreground leading-relaxed mb-10">
              {t('about.mission')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300"
              >
                {t('about.ctaPrimary')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => scrollToSection('portfolio')}
                className="hover:border-primary hover:text-primary hover:-translate-y-0.5 transition-all duration-300"
              >
                {t('about.ctaSecondary')}
              </Button>
            </div>
          </motion.div>

          {/* Value cards */}
          <motion.div variants={fadeRight} className="grid gap-5">
            {values.map(({ icon: Icon, key }) => (
              <motion.div
                key={key}
                whileHover={{ x: 6 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Card className="border-l-4 border-l-primary hover:shadow-md transition-shadow duration-300">
                  <CardHeader className="flex flex-row items-center gap-4 pb-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{t(`about.values.${key}.title`)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">{t(`about.values.${key}.description`)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  SERVICES                                       */
/* ═══════════════════════════════════════════════ */
export function ServicesSection() {
  const { t } = useLanguage();
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleServiceClick = (service: Service) => {
    setSelectedService(service);
    setModalOpen(true);
  };

  return (
    <section className="py-28 bg-muted/20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">{t('nav.services')}</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('services.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('services.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, idx) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div key={service.id} variants={staggerItem} className="h-full">
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  className="h-full"
                >
                  <Card className="h-full flex flex-col group hover:shadow-xl hover:shadow-primary/10 hover:border-primary/30 transition-all duration-300 cursor-pointer overflow-hidden">
                    <div className="h-1 bg-gradient-to-r from-primary/60 to-primary w-0 group-hover:w-full transition-all duration-500" />
                    <CardHeader className="pb-3">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                        {IconComponent && <IconComponent className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors duration-300" />}
                      </div>
                      <CardTitle className="text-lg group-hover:text-primary transition-colors duration-300">
                        {t(`services.service${idx + 1}.name`)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <CardDescription className="mb-5 text-sm leading-relaxed flex-1">
                        {t(`services.service${idx + 1}.description`)}
                      </CardDescription>
                      <div
                        className="text-2xl font-bold text-primary mb-5"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        ${service.startingPrice.toLocaleString()}+
                      </div>
                      <Button
                        variant="outline"
                        className="w-full mt-auto group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300"
                        onClick={() => handleServiceClick(service)}
                      >
                        {t('services.learnMore')}
                        <ArrowRight className="ml-2 w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      <ServiceModal service={selectedService} open={modalOpen} onOpenChange={setModalOpen} />
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  STATS BAR                                      */
/* ═══════════════════════════════════════════════ */
export function StatsBarSection() {
  const { t } = useLanguage();

  const stats = [
    { label: t('statsBar.yearsLabel'), value: t('statsBar.years'), num: 10, suffix: '+', prefix: '' },
    { label: t('statsBar.projectsLabel'), value: t('statsBar.projects'), num: 300, suffix: '+', prefix: '' },
    { label: t('statsBar.ratingLabel'), value: t('statsBar.rating'), num: 5, suffix: '.0 ★', prefix: '' },
    { label: t('statsBar.licensedLabel'), value: t('statsBar.licensed'), num: 100, suffix: '%', prefix: '' },
  ];

  return (
    <section className="py-20 bg-secondary text-secondary-foreground relative overflow-hidden">
      {/* Decorative gold lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, #c9a84c 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center"
        >
          {stats.map((stat, i) => (
            <motion.div key={i} variants={staggerItem} className="group">
              <div
                className="text-4xl md:text-5xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform duration-300"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {stat.value}
              </div>
              <div className="text-secondary-foreground/60 text-sm uppercase tracking-widest font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  PORTFOLIO                                      */
/* ═══════════════════════════════════════════════ */
export function PortfolioSection() {
  const { t } = useLanguage();
  const [filter, setFilter] = useState<'all' | 'flooring' | 'cabinets' | 'interior'>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const filteredProjects = filter === 'all'
    ? portfolioProjects
    : portfolioProjects.filter(p => p.category === filter);

  const filterButtons = [
    { key: 'all', label: t('portfolio.filterAll') },
    { key: 'flooring', label: t('portfolio.filterFlooring') },
    { key: 'cabinets', label: t('portfolio.filterCabinets') },
    { key: 'interior', label: t('portfolio.filterInterior') },
  ] as const;

  return (
    <section className="py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-14"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">{t('nav.portfolio')}</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('portfolio.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
            {t('portfolio.subtitle')}
          </p>

          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-3">
            {filterButtons.map(({ key, label }) => (
              <motion.button
                key={key}
                onClick={() => setFilter(key)}
                whileTap={{ scale: 0.97 }}
                className={`px-6 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 ${
                  filter === key
                    ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/25'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary bg-background'
                }`}
              >
                {label}
              </motion.button>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              whileHover={{ y: -6 }}
            >
              <Card
                className="overflow-hidden hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 cursor-pointer group border-0 shadow-md"
                onClick={() => { setSelectedProject(project); setModalOpen(true); }}
              >
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={project.coverImage}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Always-visible dark gradient at bottom for badge readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  {/* Hover overlay darkens for CTA */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* Location badge — always visible at bottom */}
                  <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                    <MapPin className="w-3 h-3 text-primary" />
                    {project.location}, {project.province}
                  </div>
                  {/* Hover overlay CTA */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold flex items-center gap-2 shadow-lg">
                      {t('portfolio.viewDetails')} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg group-hover:text-primary transition-colors duration-300">{project.title}</CardTitle>
                  <CardDescription>
                    <div className="flex justify-between items-center mt-1 font-mono text-xs">
                      <span className="text-primary font-semibold">${project.budget.min.toLocaleString()}–${project.budget.max.toLocaleString()}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{project.duration}</span>
                    </div>
                  </CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <ProjectModal project={selectedProject} open={modalOpen} onOpenChange={setModalOpen} />
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  PROCESS                                        */
/* ═══════════════════════════════════════════════ */
export function ProcessSection() {
  const { t } = useLanguage();

  return (
    <section className="py-28 bg-muted/20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">{t('nav.process')}</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('process.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('process.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative"
        >
          {/* Connector line on desktop */}
          <div className="hidden lg:block absolute top-14 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-primary/20 via-primary/60 to-primary/20" />

          {processSteps.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            return (
              <motion.div key={step.id} variants={staggerItem} className="relative text-center">
                {/* Step number circle */}
                <div className="relative inline-flex mb-6">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex flex-col items-center justify-center mx-auto">
                    <span
                      className="text-3xl font-bold text-primary"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      0{step.number}
                    </span>
                    {IconComponent && <IconComponent className="w-5 h-5 text-primary/70 mt-1" />}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {t(`process.step${index + 1}.title`)}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t(`process.step${index + 1}.description`)}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  REVIEWS                                        */
/* ═══════════════════════════════════════════════ */
export function ReviewsSection() {
  const { t } = useLanguage();

  return (
    <section className="py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">{t('nav.reviews')}</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('reviews.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('reviews.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {reviews.map((review) => (
            <motion.div key={review.id} variants={staggerItem} whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
              <Card className="h-full hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 border hover:border-primary/20 relative overflow-hidden">
                {/* Gold accent corner */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                  <div className="absolute top-0 right-0 w-0 h-0 border-l-[64px] border-l-transparent border-t-[64px] border-t-primary/10" />
                </div>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-primary/20 mb-2" />
                  <CardDescription className="text-base leading-relaxed italic text-foreground/80">
                    "{review.text}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 pt-2 border-t border-border/50">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{review.name}</div>
                      <div className="text-xs text-muted-foreground">{review.location}, {review.province}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  PRICING                                        */
/* ═══════════════════════════════════════════════ */
export function PricingSection() {
  const { t } = useLanguage();

  return (
    <section className="py-28 bg-muted/20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">{t('nav.pricing')}</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('pricing.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('pricing.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              variants={staggerItem}
              whileHover={{ y: -8 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Card className={`h-full relative overflow-hidden transition-all duration-300 ${
                tier.highlighted
                  ? 'border-2 border-primary bg-secondary text-secondary-foreground shadow-2xl shadow-primary/25 scale-105'
                  : 'hover:border-primary/40 hover:shadow-lg'
              }`}>
                {tier.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
                )}
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground text-xs px-4 py-1 shadow-lg">
                      ★ Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="pt-8">
                  <CardTitle className={`text-2xl ${tier.highlighted ? 'text-secondary-foreground' : ''}`} style={{ fontFamily: 'DM Serif Display, serif' }}>
                    {t(`pricing.tier${index + 1}.name`)}
                  </CardTitle>
                  <p className={`text-sm mt-1 ${tier.highlighted ? 'text-secondary-foreground/70' : 'text-muted-foreground'}`}>
                    {t(`pricing.tier${index + 1}.description`)}
                  </p>
                  <div className="mt-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <span className="text-4xl font-bold text-primary">
                      ${tier.priceRange.min.toLocaleString()}
                    </span>
                    <span className={`text-sm ml-1 ${tier.highlighted ? 'text-secondary-foreground/60' : 'text-muted-foreground'}`}>
                      – ${tier.priceRange.max.toLocaleString()}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`h-px mb-6 ${tier.highlighted ? 'bg-secondary-foreground/20' : 'bg-border'}`} />
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((_, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className={`text-sm ${tier.highlighted ? 'text-secondary-foreground/80' : 'text-muted-foreground'}`}>
                          {t(`pricing.tier${index + 1}.feature${i + 1}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => scrollToSection('contact')}
                    className={`w-full font-semibold transition-all duration-300 ${
                      tier.highlighted
                        ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25'
                        : 'variant-outline border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                    }`}
                    variant={tier.highlighted ? 'default' : 'outline'}
                  >
                    {t('pricing.getStarted')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  FAQ                                            */
/* ═══════════════════════════════════════════════ */
export function FAQSection() {
  const { t } = useLanguage();

  return (
    <section className="py-28 bg-background">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">FAQ</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('faq.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground">{t('faq.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="border border-border/60 rounded-xl px-6 data-[state=open]:border-primary/40 data-[state=open]:shadow-md data-[state=open]:shadow-primary/5 transition-all duration-300"
              >
                <AccordionTrigger className="text-left font-semibold hover:text-primary hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <span className="text-primary font-mono text-sm mt-0.5 flex-shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span>{t(`faq.q${index + 1}`)}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-10">
                  {t(`faq.a${index + 1}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  BLOG                                           */
/* ═══════════════════════════════════════════════ */
export function BlogSection() {
  const { t } = useLanguage();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section className="py-28 bg-muted/20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">Blog</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('blog.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('blog.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid md:grid-cols-3 gap-8"
        >
          {blogPosts.map((post) => (
            <motion.div
              key={post.id}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Card
                className="overflow-hidden hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 cursor-pointer h-full group border hover:border-primary/30"
                onClick={() => { setSelectedPost(post); setModalOpen(true); }}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.thumbnail}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs">
                    {post.category}
                  </Badge>
                </div>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <Clock className="w-3 h-3" />
                    <span>{post.readTime}</span>
                  </div>
                  <CardTitle className="text-lg leading-snug group-hover:text-primary transition-colors duration-300">{post.title}</CardTitle>
                  <CardDescription className="line-clamp-2 text-sm">{post.excerpt}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="ghost" className="w-full border border-border/60 group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300">
                    {t('blog.readMore')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <BlogModal post={selectedPost} open={modalOpen} onOpenChange={setModalOpen} />
    </section>
  );
}

/* ═══════════════════════════════════════════════ */
/*  CONTACT                                        */
/* ═══════════════════════════════════════════════ */
export function ContactSection() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', service: '', description: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  const contactItems: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; href: string | undefined }[] = [
    { icon: Phone, label: t('contact.info.phone'), value: COMPANY_INFO.PHONE, href: `tel:${COMPANY_INFO.PHONE}` },
    { icon: Mail, label: t('contact.info.email'), value: COMPANY_INFO.EMAIL, href: `mailto:${COMPANY_INFO.EMAIL}` },
    { icon: MapPin, label: t('contact.info.address'), value: COMPANY_INFO.ADDRESS, href: undefined },
    { icon: Users, label: t('contact.info.serviceArea'), value: t('contact.info.serviceAreaValue'), href: undefined },
  ];

  return (
    <section className="py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] font-semibold mb-3">{t('contact.info.title')}</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-5" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {t('contact.title')}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-5 rounded-full" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('contact.subtitle')}</p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto"
        >
          {/* Contact info */}
          <motion.div variants={fadeLeft} className="space-y-6">
            <div className="bg-secondary rounded-2xl p-8">
              <h3 className="text-xl font-bold text-secondary-foreground mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
                {t('contact.info.title')}
              </h3>
              {contactItems.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4 mb-6 last:mb-0">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-secondary-foreground/60 text-xs uppercase tracking-widest mb-1 font-medium">{label}</div>
                    {href ? (
                      <a href={href} className="text-secondary-foreground hover:text-primary transition-colors text-sm font-medium">
                        {value}
                      </a>
                    ) : (
                      <span className="text-secondary-foreground text-sm">{value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div variants={fadeRight}>
            <Card className="shadow-xl shadow-primary/5 border-0 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40" />
              <CardHeader className="pb-4">
                <CardTitle style={{ fontFamily: 'DM Serif Display, serif' }}>{t('contact.title')}</CardTitle>
                <CardDescription>{t('contact.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Thank you!</h3>
                    <p className="text-muted-foreground text-sm">We'll be in touch within 24 hours.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder={t('contact.form.namePlaceholder')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="focus:border-primary transition-colors"
                      />
                      <Input
                        type="tel"
                        placeholder={t('contact.form.phonePlaceholder')}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="focus:border-primary transition-colors"
                      />
                    </div>
                    <Input
                      type="email"
                      placeholder={t('contact.form.emailPlaceholder')}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="focus:border-primary transition-colors"
                    />
                    <Select value={formData.service || 'none'} onValueChange={(v) => setFormData({ ...formData, service: v === 'none' ? '' : v })}>
                      <SelectTrigger className="focus:border-primary transition-colors">
                        <SelectValue placeholder={t('contact.form.servicePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled>{t('contact.form.servicePlaceholder')}</SelectItem>
                        <SelectItem value="flooring">{t('contact.form.serviceFlooring')}</SelectItem>
                        <SelectItem value="cabinets">{t('contact.form.serviceCabinets')}</SelectItem>
                        <SelectItem value="interior">{t('contact.form.serviceInterior')}</SelectItem>
                        <SelectItem value="renovation">{t('contact.form.serviceRenovation')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea
                      placeholder={t('contact.form.descriptionPlaceholder')}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      required
                      className="focus:border-primary transition-colors resize-none"
                    />
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 transition-all duration-300"
                    >
                      {t('contact.form.submit')}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
